/**
 * Common types that are the same in both business logic usage and in stored form (inline in the document)
 */
import { PluginType } from '@accedo/one-plugin-sdk';
import { IconNames, IconStyles } from '@dynamic/ui';

import { MenuItem } from '#/client';

import {
  ButtonShape,
  ButtonVariantConfig,
  MenuBackgroundStyle,
  MenuFocusStyle,
  ScrollBehaviour,
} from '../constants';
import { PartialDictionary } from '../utils';

export interface RuntimeConfig {
  restrictions?: {
    geoblocked: boolean;
  };
  seo?: {
    title: string;
    defaultTitle?: string;
    description: string;
  };
  social?: { [key: string]: Object };
}

/** All possible page aliases. Extend it when necessary */
export type PageAliasesConfig = {
  /** Landing page */
  HOME: string;

  /** Single video / movie page */
  VIDEO: string;

  /** Main page for a given TV Series */
  TV_SHOW: string;

  /** Page for a given episode from a TV Series */
  TV_SHOW_EPISODE?: string;

  /** Watch History page */
  WATCH_HISTORY: string;

  /** Search page */
  SEARCH: string;

  /** Favorites page */
  FAVORITES: string;
};

export type FontWeight = {
  [key: string]: string | number;
};

export type FontFamily = {
  [key: string]: string | number;
};

export type TLogo = {
  primary?: string;
  secondary?: string;
};

export type ThemeIcon = {
  navigation: {
    style: IconStyles;
  };
  parentalGuidance: {
    style: Omit<IconStyles, IconStyles.Modern>;
  };
};

export interface FontsDetails {
  textClasses: TextClasses;
  // It's not used in Web client, however Dynamic UI still requires these two
  heading: string;
  paragraph: string;
}

export type TextClasses = {
  [key: string]: TextClass;
};

export type TextClass = {
  size: number;
  lineHeight: number;
  style: string;
  type: string;
  weight: string;
};

export type TButtonShape = PartialDictionary<
  ButtonVariantConfig,
  { shape: ButtonShape }
>;

interface SocialItem {
  key: string;
  icon: IconNames;
  action: object;
}

interface FooterItem {
  key: string;
  title: string;
  action: CommonAction;
}

export interface FooterColumn {
  key: string;
  title: string;
  items: FooterItem[];
}

export type FooterInfo = {
  copyright: string;
  contact: string;
  socials: SocialItem[];
  isMobile?: boolean;
};

export type FooterConfig = {
  enabled: boolean;
  copyright: string;
  contact: string;
  socials: SocialItem[];
  columns?: FooterColumn[];
};

export type TermsConfig = {
  body: string;
  date: string;
};

export type THamburgerItemsData = {
  mainMenuItems: MenuItem[];
  linkMenuItems: MenuItem[];
  showItems: boolean;
  toggleHamburger: () => void;
  focusStyle?: MenuFocusStyle;
};

export type CommonAction = {
  /** @deprecated Use `kind` instead */
  type?: string;
  /** @deprecated Use `parameters` instead */
  params?: {
    destination?: {
      [key: string]: string;
    };
    page?: string;
    submit?: string;
  };
  text?: string;
  kind?: string;
  parameters?: {
    link?: string;
    view?: string;
    pageId?: string;
  };
};

export interface MenuStyle {
  background?: MenuBackgroundStyle;
  focus?: MenuFocusStyle;
  scrollBehaviour?: ScrollBehaviour;
}

export interface MenusConfig {
  groups: string[];
  style: MenuStyle;
}

/** Describes what events and params to send to which analytics vendors. */
export interface AnalyticsEventsConfig {
  // internal event name, AnalyticsEventType, ex: "screen.view"
  [event: string]: {
    // analytics vendor, bundle identifier, ex: "com.google.analytics4"
    [vendor: string]: {
      // external event name, arbitrary, ex "screenView" for GA4
      event: string;
      properties: {
        // external property object, ex: "screen_name": { "string": "{{ screen.title }}" }
        [property: string]: object;
      };
    }[];
  };
}

/** Features that Core integration configs can be active for. */
export enum IntegrationsFeatures {
  ANALYTICS = 'ANALYTICS',
}

/** Integration configuration from Core. */
export interface IntegrationsConfig {
  // integration vendor, bundle identifier, ex: "com.google.analytics4"
  [vendor: string]: {
    // what features is this integration active for
    activeForFeatures: IntegrationsFeatures[];
    // arbitrary configuration
    configuration: object;
  };
}

/** Describes which plugin to load for a given Core integration config. */
export interface PluginIntegrationsConfig {
  // integration vendor, bundle identifier, ex: "com.google.analytics4"
  [vendor: string]: {
    // same as plugin.src in features config, just called differently
    source: string;
    id: string;
    type: PluginType;
  };
}
